import type { ComputedRef, Ref } from 'vue'
import type { CustomerCreditsAdjustment } from '@entities/customer'

import { useCustomerCreditsStore } from './store'
import { CREDITS_HISTORY_PER_PAGE } from '../../api/credits/credits.hook'
import { useCustomerCreditsAdjustApi } from '../../api/credits/credits.hook'
import { useCustomerCreditsHistoryApi } from '../../api/credits/credits.hook'
import { useCustomerReferralProgramApi } from '../../api/credits/credits.hook'

const CENTS_PER_DOLLAR = 100

interface ModalCustomerCreditsReturn {
  refIsOpen: ComputedRef<boolean>
  refIsProcessing: ComputedRef<boolean>
  refAdjustment: Ref<CustomerCreditsAdjustment>
  refAmount: Ref<string | number>
  isSubmitDisabled: ComputedRef<boolean>
  setAdjustment: (adjustment: CustomerCreditsAdjustment) => void
  setAmount: (amount: string | number) => void
  close: () => void
  submit: () => Promise<void>
}

/** "Edit credits": top up or remove an amount typed in dollars; the API takes positive cents either way. */
export const useModalCustomerCredits = (): ModalCustomerCreditsReturn => {
  const route = useRoute()
  const store = useCustomerCreditsStore()

  const refAdjustment = ref<CustomerCreditsAdjustment>('add')
  const refAmount = ref<string | number>(0)

  const refIsOpen = computed<boolean>(() => store.isModalEdit)
  const refIsProcessing = computed<boolean>(() => store.isProcessing)
  const amountCents = computed<number>(() => Math.round(Number(refAmount.value || 0) * CENTS_PER_DOLLAR))
  const isSubmitDisabled = computed<boolean>(() => refIsProcessing.value || !(amountCents.value > 0))

  const setAdjustment = (adjustment: CustomerCreditsAdjustment): void => {
    refAdjustment.value = adjustment
  }

  const setAmount = (amount: string | number): void => {
    refAmount.value = amount
  }

  const close = (): void => {
    if (store.isProcessing) return

    store.setModalEdit(false)
    refAdjustment.value = 'add'
    refAmount.value = 0
  }

  const submit = async (): Promise<void> => {
    if (isSubmitDisabled.value) return

    const customerId = route.params.id as string
    const isDone = await useCustomerCreditsAdjustApi({
      customerId,
      adjustment: refAdjustment.value,
      amount: amountCents.value,
    })

    close()

    if (isDone) {
      await Promise.all([
        useCustomerReferralProgramApi(customerId),
        useCustomerCreditsHistoryApi({ customerId, page: 1, perPage: CREDITS_HISTORY_PER_PAGE }),
      ])
    }
  }

  return {
    refIsOpen,
    refIsProcessing,
    refAdjustment,
    refAmount,
    isSubmitDisabled,
    setAdjustment,
    setAmount,
    close,
    submit,
  }
}
